import { useTranslation } from "react-i18next";
import { Control, useFieldArray } from "react-hook-form";
import { Button, IconButton } from "@carbon/react";
import { Add, TrashCan } from "@carbon/icons-react";
import TextField from "../carbon/Form/TextField";
import { EditSiteFormValues } from "./editSiteValidationSchema";

/**
 * The "Delivery configuration" section of `EditSiteForm` (Task 4.3, Purchase Orders): lead time,
 * delivery windows, delivery requirements and default recipients. Every field is optional. Binds
 * to the SAME `control` the parent form owns (three `useFieldArray`s, no local state), so the
 * values ride the existing Site save. Visibility is the parent route's: `/sites/:id` is already
 * behind `RequireAbility(MANAGE_SITES)`.
 */
const SiteDeliveryConfigFields = ({
  control,
}: {
  control: Control<EditSiteFormValues>;
}) => {
  const { t } = useTranslation();

  const windows = useFieldArray({ control, name: "deliveryWindows" });
  const requirements = useFieldArray({ control, name: "deliveryRequirements" });
  const recipients = useFieldArray({ control, name: "defaultRecipients" });

  const removeButton = (label: string, onClick: () => void) => (
    <IconButton kind="ghost" label={label} align="top" onClick={onClick}>
      <TrashCan />
    </IconButton>
  );

  return (
    <section className="edit-site-form__delivery-config">
      <h3>{t("sites.deliveryConfig.title")}</h3>
      <p className="edit-site-form__delivery-config-description">
        {t("sites.deliveryConfig.description")}
      </p>

      <div className="edit-site-form__grid">
        <TextField
          name="leadTimeDays"
          label={t("sites.deliveryConfig.leadTimeDays")}
          control={control}
          type="number"
          min={0}
          optional
        />
      </div>

      <div className="edit-site-form__subsection">
        <h4>{t("sites.deliveryConfig.windows")}</h4>
        {windows.fields.map((field, index) => (
          <div key={field.id} className="edit-site-form__row">
            <TextField
              name={`deliveryWindows.${index}.value`}
              label={t("sites.deliveryConfig.windowLabel")}
              control={control}
            />
            {removeButton(t("cta.remove"), () => windows.remove(index))}
          </div>
        ))}
        <Button
          kind="tertiary"
          size="sm"
          type="button"
          renderIcon={Add}
          onClick={() => windows.append({ value: "" })}
        >
          {t("sites.deliveryConfig.addWindow")}
        </Button>
      </div>

      <div className="edit-site-form__subsection">
        <h4>{t("sites.deliveryConfig.requirements")}</h4>
        {requirements.fields.map((field, index) => (
          <div key={field.id} className="edit-site-form__row">
            <TextField
              name={`deliveryRequirements.${index}.title`}
              label={t("sites.deliveryConfig.requirementTitle")}
              control={control}
            />
            <TextField
              name={`deliveryRequirements.${index}.detail`}
              label={t("sites.deliveryConfig.requirementDetail")}
              control={control}
            />
            {removeButton(t("cta.remove"), () => requirements.remove(index))}
          </div>
        ))}
        <Button
          kind="tertiary"
          size="sm"
          type="button"
          renderIcon={Add}
          onClick={() => requirements.append({ title: "", detail: "" })}
        >
          {t("sites.deliveryConfig.addRequirement")}
        </Button>
      </div>

      <div className="edit-site-form__subsection">
        <h4>{t("sites.deliveryConfig.recipients")}</h4>
        {recipients.fields.map((field, index) => (
          <div key={field.id} className="edit-site-form__row">
            <TextField
              name={`defaultRecipients.${index}.name`}
              label={t("sites.deliveryConfig.recipientName")}
              control={control}
            />
            <TextField
              name={`defaultRecipients.${index}.email`}
              label={t("sites.deliveryConfig.recipientEmail")}
              control={control}
              type="email"
            />
            {removeButton(t("cta.remove"), () => recipients.remove(index))}
          </div>
        ))}
        <Button
          kind="tertiary"
          size="sm"
          type="button"
          renderIcon={Add}
          onClick={() => recipients.append({ name: "", email: "" })}
        >
          {t("sites.deliveryConfig.addRecipient")}
        </Button>
      </div>
    </section>
  );
};

export default SiteDeliveryConfigFields;
